import { Box, Button, Paper, Typography, useTheme } from "@mui/material";

import { formatDecimal } from "@aclymatepackages/formatters";
import { hexToRgba } from "@aclymatepackages/converters";

import Link from "../../../atoms/mui/Link";
import ScoutNavigateCard from "../../scout/cards/ScoutNavigateCard";
import { resolveDisplayTotalTons } from "../../views/dashboardHelpers";
import { usePendingGateActivity } from "../../../../helpers/components/explorerPendingGates";

import EstimateProgressCard from "./EstimateProgressCard";
import MyFootprintCard from "./MyFootprintCard";
import ExplorerOverview from "./ExplorerOverview";

const QUICK_LINKS = [
  {
    label: "My Suppliers",
    href: "/explorer/dashboard?view=suppliers",
    description: "Review emissions data your suppliers shared.",
    gate: "suppliers"
  },
  {
    label: "My Product BOM Requests",
    href: "/explorer/dashboard?view=products",
    description: "Respond to bill-of-materials requests from customers.",
    gate: "products"
  },
  {
    label: "My Emissions Requests",
    href: "/explorer/dashboard?view=customers",
    description: "Respond to customer emissions requests.",
    gate: "customers"
  }
];

const SUPPLIERS_HREF = "/explorer/dashboard?view=suppliers";

const NextBestActionCard = ({ action }) => {
  const { palette } = useTheme();
  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: "8px",
        flex: 1,
        minWidth: 240,
        display: "flex",
        flexDirection: "column",
        gap: 1.5
      }}
    >
      <Typography variant="subtitle1" color="textPrimary">
        Next best action
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {action.description}
      </Typography>
      <Box mt="auto">
        <Link href={action.href} style={{ textDecoration: "none" }}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            sx={{ color: palette.common.white }}
          >
            {action.label}
          </Button>
        </Link>
      </Box>
    </Paper>
  );
};

const QuickLinks = ({ gateActivity }) => {
  const availableLinks = QUICK_LINKS.filter(({ gate }) => gateActivity[gate]);

  if (!availableLinks.length) {
    return null;
  }

  return (
    <Box>
      <Box mb={1}>
        <Typography variant="subtitle1" color="textPrimary">
          Quick links
        </Typography>
      </Box>
      <Box display="flex" gap={2} flexWrap="wrap">
        {availableLinks.map((link) => (
          <Box key={link.label} flex="1 1 220px" minWidth={220}>
            <ScoutNavigateCard
              label={link.label}
              href={link.href}
              description={link.description}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const SupplierSurveyImpactCard = ({ totalSurveyTons, surveySupplierCount }) => (
  <Paper
    elevation={1}
    sx={{
      p: 2,
      borderRadius: "8px",
      flex: "1 1 320px",
      minWidth: 280,
      display: "flex",
      flexDirection: "column",
      gap: 1
    }}
  >
    <Typography variant="subtitle1" color="textPrimary">
      Supply Chain Total Impact
    </Typography>
    <Typography variant="caption" color="textSecondary">
      Total Scope 3 emissions based on your spend estimates.
    </Typography>
    <Box display="flex" alignItems="baseline" gap={1} mt={1}>
      <Typography variant="h4" color="textPrimary">
        {formatDecimal(Number(totalSurveyTons) || 0)}
      </Typography>
      <Typography variant="subtitle2" color="textSecondary">
        tCO₂e
      </Typography>
    </Box>
    <Typography variant="caption" color="textSecondary">
      {surveySupplierCount === 1
        ? "From 1 supplier"
        : `From ${surveySupplierCount} suppliers`}
    </Typography>
  </Paper>
);

const TopSuppliersCard = ({ topSupplierEmissions, suppliersActive }) => {
  const { palette } = useTheme();
  const hasSuppliers = topSupplierEmissions.length > 0;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: "8px",
        flex: "1 1 320px",
        minWidth: 280,
        display: "flex",
        flexDirection: "column",
        gap: 1
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" color="textPrimary">
          Supply Chain Estimates by Category
        </Typography>
        {suppliersActive && (
          <Link href={SUPPLIERS_HREF} styleProps={{ textDecoration: "none" }}>
            <Typography variant="caption" color="secondary">
              View all supplier surveys →
            </Typography>
          </Link>
        )}
      </Box>
      <Typography variant="caption" color="textSecondary">
        Top supply chain scope 3 emissions
      </Typography>
      {!hasSuppliers && (
        <Box mt={1}>
          <Typography variant="body2" color="textSecondary">
            No supplier emissions captured yet. Invite suppliers to see their
            impact here.
          </Typography>
        </Box>
      )}
      {hasSuppliers && (
        <Box mt={1} display="flex" flexDirection="column" gap={0.75}>
          {topSupplierEmissions.map((supplier, idx) => (
            <Box
              key={supplier.name}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{
                py: 0.5,
                borderBottom:
                  idx < topSupplierEmissions.length - 1
                    ? `1px solid ${palette.backgroundGray.main}`
                    : "none"
              }}
            >
              <Typography variant="body2" color="textPrimary" noWrap>
                {supplier.name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`${formatDecimal(supplier.tons)} tCO₂e`}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

const FootprintReadyBanner = ({ year, displayTotalTons }) => {
  const { palette } = useTheme();
  return (
    <Box
      p={2}
      sx={{
        borderRadius: "8px",
        bgcolor: hexToRgba(palette.secondary.light, 0.18),
        border: `1px solid ${hexToRgba(palette.secondary.main, 0.4)}`
      }}
    >
      <Typography variant="subtitle1" color="textPrimary">
        {`Your ${year} footprint estimate is ready 🎉`}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {`${formatDecimal(displayTotalTons)} tCO₂e measured. Confidence: Low — Explorer uses benchmark estimates.`}
      </Typography>
    </Box>
  );
};

const DashboardHome = ({
  surfaceContext,
  surveyEmissions,
  nextBestAction,
  topSupplierEmissions,
  totalSurveyTons,
  surveySupplierCount
}) => {
  const { year, totalTons, completenessSummary, surveyStatus, hotspots } =
    surfaceContext;
  const surveyComplete = surveyStatus === "complete";
  const gateActivity = usePendingGateActivity();
  const displayTotalTons = resolveDisplayTotalTons({
    totalTons,
    surveyEmissions
  });

  return (
    <Box display="flex" flexDirection="column" gap={3} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="h4" color="textPrimary">
          Dashboard
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Estimate your footprint, understand what matters most, and see your
          next steps.
        </Typography>
      </Box>

      {surveyComplete && (
        <FootprintReadyBanner year={year} displayTotalTons={displayTotalTons} />
      )}

      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        <EstimateProgressCard
          percent={completenessSummary?.percent}
          year={year}
          surveyStatus={surveyStatus}
          completenessSummary={completenessSummary}
        />
        <MyFootprintCard totalTons={displayTotalTons} />
        <NextBestActionCard action={nextBestAction} />
      </Box>

      <QuickLinks gateActivity={gateActivity} />
      <ExplorerOverview surveyEmissions={surveyEmissions} hotspots={hotspots} />
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="stretch">
        <SupplierSurveyImpactCard
          totalSurveyTons={totalSurveyTons}
          surveySupplierCount={surveySupplierCount}
        />
        <TopSuppliersCard
          topSupplierEmissions={topSupplierEmissions}
          suppliersActive={gateActivity.suppliers}
        />
      </Box>
    </Box>
  );
};

export default DashboardHome;
